// Cross-browser download helpers for CSV and XLSX formats.
// XLSX is produced with styled headers, auto column widths, and currency/date formatting via exceljs.

import ExcelJS from 'exceljs';

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Delay cleanup so the browser has time to start the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
}

export function downloadBlob(blob, filename) {
  triggerDownload(blob, filename);
}

export function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return false;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((k) => escape(r[k])).join(',')),
  ];
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename);
  return true;
}

// Infer column kind from field name so we can format cells correctly.
// Order matters: dates first (so 'expense_date' is not mistaken for currency),
// then quantity (so 'total_yield' is not mistaken for currency), then currency.
const DATE_KEYS = /(_date|^date|_at|timestamp)$/i;
const QUANTITY_KEYS = /(quantity|yield|weight|harvested|sold|packets|num_)/i;
const CURRENCY_KEYS = /(amount|total|paid|price|revenue|balance|expense|cash|credit|pending|purchase|received|given|rate)/i;

function inferKind(key, override) {
  if (override && override[key]) return override[key];
  if (DATE_KEYS.test(key)) return 'date';
  if (QUANTITY_KEYS.test(key)) return 'number';
  if (CURRENCY_KEYS.test(key)) return 'currency';
  return 'text';
}

function prettyHeader(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function toDateIfIso(v) {
  if (v instanceof Date) return v;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}[T ]/.test(v)) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d;
  }
  return v;
}

export async function downloadXLSX(rows, filename, { sheetName = 'Sheet1', title = null, columnTypes = null } = {}) {
  if (!rows || rows.length === 0) return false;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mushroom Farm Manager';
  wb.created = new Date();
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: title ? 2 : 1 }],
  });

  const keys = Object.keys(rows[0]);
  const columns = keys.map((key) => {
    const kind = inferKind(key, columnTypes);
    let numFmt;
    if (kind === 'currency') numFmt = '"Rs."#,##0';
    else if (kind === 'number') numFmt = '#,##0';
    else if (kind === 'date') numFmt = 'yyyy-mm-dd hh:mm';
    return { header: prettyHeader(key), key, width: Math.max(14, prettyHeader(key).length + 4), style: numFmt ? { numFmt } : undefined };
  });

  if (title) {
    ws.mergeCells(1, 1, 1, keys.length);
    const c = ws.getCell(1, 1);
    c.value = title;
    c.font = { bold: true, size: 14, color: { argb: 'FF1A2620' } };
    c.alignment = { vertical: 'middle', horizontal: 'left' };
    ws.getRow(1).height = 22;
    ws.addRow([]); // spacer
  }

  ws.columns = columns;

  // Re-add header row with our preferred styling (exceljs sets it automatically from columns,
  // but we style it after the fact)
  const headerRowNumber = title ? 3 : 1;
  if (title) {
    // Manually add header row since ws.columns already created headers at row 1
    const headerRow = ws.getRow(3);
    keys.forEach((k, i) => {
      headerRow.getCell(i + 1).value = prettyHeader(k);
    });
    headerRow.commit();
  }

  const headerRow = ws.getRow(headerRowNumber);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2C4A3B' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE4DFD3' } },
      left: { style: 'thin', color: { argb: 'FFE4DFD3' } },
      bottom: { style: 'thin', color: { argb: 'FFE4DFD3' } },
      right: { style: 'thin', color: { argb: 'FFE4DFD3' } },
    };
  });
  headerRow.height = 20;

  // Data rows
  rows.forEach((r) => {
    const row = {};
    keys.forEach((k) => {
      const kind = inferKind(k, columnTypes);
      let v = r[k];
      if (kind === 'date') v = toDateIfIso(v);
      else if (kind === 'currency' || kind === 'number') {
        const n = typeof v === 'number' ? v : parseFloat(v);
        v = isFinite(n) ? n : (v ?? '');
      } else if (v == null) v = '';
      row[k] = v;
    });
    ws.addRow(row);
  });

  // Alternate row striping for readability
  const dataStart = headerRowNumber + 1;
  for (let i = dataStart; i <= ws.rowCount; i++) {
    const zebra = (i - dataStart) % 2 === 1;
    const row = ws.getRow(i);
    row.eachCell((cell) => {
      if (zebra) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F5F0' } };
      }
      cell.border = {
        bottom: { style: 'hair', color: { argb: 'FFE4DFD3' } },
      };
    });
  }

  // Adjust column widths based on content length (cap at 40)
  ws.columns.forEach((col, idx) => {
    const key = keys[idx];
    let maxLen = prettyHeader(key).length;
    rows.forEach((r) => {
      const v = r[key];
      if (v != null) {
        const s = v instanceof Date ? 'YYYY-MM-DD HH:MM' : String(v);
        if (s.length > maxLen) maxLen = s.length;
      }
    });
    col.width = Math.min(40, Math.max(12, maxLen + 2));
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const finalName = filename.endsWith('.xlsx') ? filename : filename.replace(/\.csv$/i, '') + '.xlsx';
  triggerDownload(blob, finalName);
  return true;
}
